import * as tslib_1 from "tslib";
import React, { useMemo, useCallback } from 'react';
import _ from 'lodash';
import { withLocalize } from 'react-localize-redux';
import { useLocalize } from '../../../hooks';
import { Text } from '../Text';
import { SelectDropdownInput } from '../../fields'; // Custom filter function that prioritizes prefix matches and improves relevance

var customFilter = function customFilter(option, inputValue) {
  if (!inputValue) return true;
  var input = inputValue.toLowerCase().trim(); // Helper function to safely convert to string

  var safeToString = function safeToString(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();

    if (React.isValidElement(value)) {
      // For JSX elements, try to extract text content or use a fallback
      return '';
    }

    return value ? String(value) : '';
  };

  var label = safeToString(option.label || '').toLowerCase();
  var value = safeToString(option.value || '').toLowerCase();
  var rawValue = safeToString(option.raw_value || '').toLowerCase(); // Check all possible text fields for matches

  var searchableText = "".concat(label, " ").concat(value, " ").concat(rawValue); // Exact prefix match (highest priority) - only match if input is at the start

  if (label.startsWith(input) || value.startsWith(input) || rawValue.startsWith(input)) {
    return true;
  } // Word boundary prefix match (high priority) - only match if input starts a word


  var words = searchableText.split(/\s+/);
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = words[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var word = _step.value;

      if (word.startsWith(input)) {
        return true;
      }
    } // Remove the general contains match - only allow prefix and word boundary matches
    // This prevents "Arizona" from matching when typing "n"

  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return false;
};

var SelectDropdownFieldComponent = function SelectDropdownFieldComponent(_a) {
  var form = _a.form,
      field = _a.field,
      id = _a.id,
      options = _a.options,
      defaultLabelId = _a.defaultLabelId,
      optionsDefaultLabelId = _a.optionsDefaultLabelId,
      label = _a.label,
      rawOptionLabels = _a.rawOptionLabels,
      useRawOptions = _a.useRawOptions,
      labelPrefix = _a.labelPrefix,
      onChanged = _a.onChanged,
      translate = _a.translate,
      siteGroup = _a.siteGroup,
      _a$isSearchable = _a.isSearchable,
      isSearchable = _a$isSearchable === void 0 ? true : _a$isSearchable,
      errorAfterSubmit = _a.errorAfterSubmit,
      menuPlacement = _a.menuPlacement,
      openMenuOnFocus = _a.openMenuOnFocus,
      menuPortalTargetId = _a.menuPortalTargetId,
      rest = tslib_1.__rest(_a, ["form", "field", "id", "options", "defaultLabelId", "optionsDefaultLabelId", "label", "rawOptionLabels", "useRawOptions", "labelPrefix", "onChanged", "translate", "siteGroup", "isSearchable", "errorAfterSubmit", "menuPlacement", "openMenuOnFocus", "menuPortalTargetId"]);

  var values = form.values,
      errors = form.errors,
      touched = form.touched,
      submitCount = form.submitCount;

  var value = _.get(values, id);

  var error = _.get(touched, id) ? _.get(errors, id) : undefined; // showError will only show errors when field is touched or after submit if errorAfterSubmit is true

  var showError = errorAfterSubmit ? !!submitCount : !!_.get(touched, id);

  var _useLocalize = useLocalize(),
      getTranslationId = _useLocalize.getTranslationId,
      getFieldTranslationId = _useLocalize.getFieldTranslationId;

  var getValueTranslationId = useCallback(function (value) {
    return getFieldTranslationId({
      id: id,
      defaultLabelId: optionsDefaultLabelId || defaultLabelId,
      option: value
    });
  }, [id, defaultLabelId, optionsDefaultLabelId, getFieldTranslationId]);
  var getOptionValue = useCallback(function (option) {
    // for searchable value, get raw translation (using non-html data to return plain string)
    var val = translate(getTranslationId({
      id: getValueTranslationId(option),
      type: 'field'
    }), {
      ndash: '-'
    });
    return _.isString(val) ? val : option;
  }, [translate, getTranslationId, getValueTranslationId]);
  var getOptionLabel = useCallback(function (option) {
    if (useRawOptions) {
      if (rawOptionLabels) {
        return rawOptionLabels[option] || option;
      }

      return option;
    }

    return React.createElement(Text, {
      id: getValueTranslationId(option),
      type: "field"
    });
  }, [useRawOptions, rawOptionLabels, getValueTranslationId]);
  var selectOptions = useMemo(function () {
    return _.map(options, function (o) {
      // clean the option value, since it's used for search (so we also store raw value - not displayed, used for matching original option on change)
      return {
        value: o ? useRawOptions ? o : getOptionValue(o) : o,
        label: o ? getOptionLabel(o) : React.createElement("div", null, React.createElement("hr", null)),
        raw_value: o
      };
    });
  }, [options, getOptionValue, getOptionLabel, useRawOptions]);

  var onChange = function onChange(_e, value) {
    if (value) {
      // Always update Formik immediately so blur-triggered validation sees the new value.
      // When onChanged is used, the parent may also call setValues - that's fine, it ensures
      // we don't validate with stale values when the dropdown blurs before the parent's update.
      form.setFieldValue(id, value);

      if (onChanged) {
        onChanged(value);
      } // Checks to see if the dropdown is part of a phone array, if so, format the id for the corresponding phoneNumber input and select the input


      var isPhoneArray = _.toLower(id).includes('phones');

      if (isPhoneArray) {
        var formattedId = id.substring(0, id.lastIndexOf('.')) + '.phoneNumber';
        var phoneNumberEl = document.getElementById(formattedId);
        phoneNumberEl.select();
      }
    }
  };

  return React.createElement(React.Fragment, null, React.createElement(SelectDropdownInput, Object.assign({
    name: id,
    label: label || React.createElement(Text, {
      id: getFieldTranslationId({
        id: id,
        defaultLabelId: defaultLabelId
      }),
      type: "field",
      data: labelPrefix && {
        prefix: labelPrefix
      },
      siteGroup: siteGroup
    }),
    isSearchable: isSearchable,
    value: value,
    error: error,
    options: selectOptions,
    onblur: field.onBlur,
    onchange: onChange,
    filterOption: isSearchable ? customFilter : undefined,
    menuPlacement: menuPlacement,
    openMenuOnFocus: openMenuOnFocus,
    menuPortalTargetId: menuPortalTargetId
  }, rest)), error && showError && React.createElement("span", {
    className: "error"
  }, React.createElement(Text, {
    id: id,
    textKey: "".concat(error),
    type: 'error',
    siteGroup: siteGroup
  })));
};

export var SelectDropdownField = withLocalize(SelectDropdownFieldComponent);